import { FC } from "react";
import { Link } from "react-router-dom";
import ShredCreating from "@/components/ShredCreating";
import AIButton from "@/components/ui/AIButton";

import { Checkbox } from "@/components/ui/Checkbox/index.ts";
import {
    Brain,
    Database,
    Layers,
    Sparkles,
    Stars,
    Wand2,
    ChevronRight
} from "lucide-react";

interface AIGenerationCardProps {
    id: string;
    serviceId: string;
    loading: boolean;
    serviceStatus: "IDLE" | "GENERATING_SHREDS" | "FAILED";
    generateShredModel: boolean;
    setGenerateShredModel: (value: boolean) => void;
    onGenerateShred: () => void;
    hasExistingShreds: boolean;
    hasDataModel: boolean;
    timeRemaining?: number;
    onRefresh: () => void;
}

const AIGenerationCard: FC<AIGenerationCardProps> = ({
                                                         id,
                                                         serviceId,
                                                         loading,
                                                         serviceStatus,
                                                         generateShredModel,
                                                         setGenerateShredModel,
                                                         onGenerateShred,
                                                         hasExistingShreds,
                                                         hasDataModel,
                                                         timeRemaining,
                                                         onRefresh
                                                     }) => {

    const getMainText = () => {
        if (!hasExistingShreds) {
            return "Generate Your First Shreds";
        }
        if (!hasDataModel) {
            return "Add Data Model Shred";
        }
        return "Generate Additional Shreds";
    };

    const getSubText = () => {
        if (!hasExistingShreds) {
            return "Let AI intelligently divide your service into manageable modules";
        }
        if (!hasDataModel) {
            return "Build the foundation with AI-powered data structures";
        }
        return "Expand your service with AI-generated feature modules";
    };

    // Show ShredCreating component when loading OR when service is generating shreds
    if (loading || serviceStatus === "GENERATING_SHREDS") {
        return (
            <div className="col-span-12 lg:col-span-6">
                <ShredCreating refetch={onRefresh} time={timeRemaining} />
            </div>
        );
    }

    return (
        <div className="col-span-12 lg:col-span-6" data-tour="generate-shreds-card">
            {/* ADD data-tour attribute to main card container */}
            <div
                className="relative overflow-hidden rounded-2xl min-h-[300px] bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 flex flex-col border border-indigo-300 shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-[1.02] group"
            >
                {/* Enhanced AI Background Effects */}
                <div className="absolute inset-0 overflow-hidden">
                    {/* Animated gradient orbs */}
                    <div className="absolute top-8 right-8 w-20 h-20 bg-gradient-to-br from-indigo-400/30 to-purple-400/30 rounded-full blur-xl animate-pulse"></div>
                    <div className="absolute bottom-8 left-8 w-16 h-16 bg-gradient-to-br from-purple-400/30 to-pink-400/30 rounded-full blur-xl animate-pulse delay-1000"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-2xl animate-pulse delay-500"></div>

                    {/* Floating particles */}
                    <div className="absolute top-12 left-12 w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-200"></div>
                    <div className="absolute top-20 right-16 w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce delay-700"></div>
                    <div className="absolute bottom-16 right-12 w-2.5 h-2.5 bg-pink-400 rounded-full animate-bounce delay-1200"></div>
                </div>

                {/* Magical Status Indicator */}
                <div className="absolute top-4 right-4 z-10">
                    <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-indigo-700 border border-indigo-300 backdrop-blur-sm">
                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 animate-pulse" />
                        <Stars className="w-3 h-3" />
                        <span>AI Powered</span>
                    </div>
                </div>

                <div className="relative z-10 p-6 flex flex-col h-full">
                    {/* Captivating Header */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl mb-4 shadow-xl relative overflow-hidden group-hover:scale-110 transition-transform duration-500">
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer"></div>
                            <Wand2 className="w-8 h-8 text-white relative z-10" />
                        </div>
                        <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 leading-tight">
                            {getMainText()}
                        </h1>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            {getSubText()}
                        </p>
                    </div>

                    {/* AI Features Showcase */}
                    <div className="flex-1 mb-6">
                        <div className="grid grid-cols-1 gap-3 mb-4">
                            {/* Smart Module Division */}
                            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200 group-hover:shadow-md transition-all">
                                <div className="flex items-start space-x-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Brain className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-semibold text-indigo-800 mb-1">Smart Service Division</h4>
                                        <p className="text-xs text-indigo-600 leading-relaxed">
                                            AI analyzes your service and intelligently divides it into optimal modules for easier management, team collaboration, and testing
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Team Benefits */}
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200 group-hover:shadow-md transition-all">
                                <div className="flex items-start space-x-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Layers className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-semibold text-purple-800 mb-1">Parallel Development</h4>
                                        <p className="text-xs text-purple-600 leading-relaxed">
                                            Enables teams to work on different modules simultaneously, improving productivity and reducing development time
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Model Checkbox - ADD data-tour attribute */}
                        {!hasDataModel && (
                            <div
                                data-tour="generate-model-toggle"
                                className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-200"
                            >
                                <label className="flex items-center space-x-3 cursor-pointer">
                                    <Checkbox
                                        checked={generateShredModel}
                                        onChange={setGenerateShredModel}
                                        color="indigo-500"
                                    />
                                    <div className="flex items-center space-x-2">
                                        <Database className="w-4 h-4 text-blue-600" />
                                        <span className="text-sm font-medium text-blue-800">Include Data Model Shred</span>
                                    </div>
                                </label>
                                <p className="text-xs text-blue-600 mt-1 ml-7">Recommended foundation</p>
                            </div>
                        )}
                    </div>

                    {/* Magical CTA Button */}
                    <div className="space-y-3">
                        <AIButton
                            variant="gradient"
                            size="xl"
                            loading={loading}
                            disabled={loading}
                            onClick={onGenerateShred}
                            icon={<Sparkles className="w-5 h-5" />}
                            showSparkles={true}
                            showArrow={false}
                            glowIntensity="high"
                        >
                            Generate with AI
                        </AIButton>

                        {/* Subtle Manual Option */}
                        <div className="text-center">
                            <Link
                                to={`/architecture/project/${id}/services/${serviceId}/create`}
                                className="inline-flex items-center text-xs text-slate-500 hover:text-slate-700 transition-colors group/manual"
                            >
                                <span>or</span>
                                <span className="mx-1 underline underline-offset-2 decoration-dotted group-hover/manual:decoration-solid">create manually</span>
                                <ChevronRight className="w-3 h-3 ml-1 group-hover/manual:translate-x-0.5 transition-transform" />
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"></div>
            </div>

            {/* Shimmer animation styles */}
            <style>{`
                @keyframes shimmer {
                    0% {
                        transform: translateX(-100%) skewX(-12deg);
                    }
                    100% {
                        transform: translateX(200%) skewX(-12deg);
                    }
                }
                
                .animate-shimmer {
                    animation: shimmer 2s infinite;
                }
            `}</style>
        </div>
    );
};

export default AIGenerationCard;